/**
 * Settings → Features (owners and admins; read-only for everyone else): one switch per product area (core
 * logic/features) with what it covers. Off hides the area in every app and blocks it; nothing is deleted. Calendar,
 * Contacts and Huddles are areas of their own; the last usable area can't be turned off. The workspace chooses what owners
 * and admins keep of a switched-off area: nothing, a read-only look, or full use. Renders WorkspaceSettingsViewModel.
 */
import type { FeatureAdminAccess, FeatureArea } from "@bundle/contracts";
import type { MessageKey, WorkspaceSettingsState, WorkspaceSettingsViewModel } from "@bundle/core";
import { SegmentedControl, Stack, Toggle } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";
import { FieldNote, SectionStatus, SettingsNotice, SettingsSection } from "./SettingsKit.tsx";

/** Each area's name and what it covers. */
export const FEATURE_COPY: Record<FeatureArea, { title: MessageKey; hint: MessageKey }> = {
  chat: { title: "features.chat.title", hint: "features.chat.hint" },
  mail: { title: "features.mail.title", hint: "features.mail.hint" },
  calendar: { title: "features.calendar.title", hint: "features.calendar.hint" },
  contacts: { title: "features.contacts.title", hint: "features.contacts.hint" },
  docs: { title: "features.docs.title", hint: "features.docs.hint" },
  huddles: { title: "features.huddles.title", hint: "features.huddles.hint" },
};

export type FeaturesSettingsProps = { vm: WorkspaceSettingsViewModel; state: WorkspaceSettingsState };

export function FeaturesSettings({ vm, state: s }: FeaturesSettingsProps) {
  const t = useT();
  if (s.status !== "ready" || s.workspace === undefined) {
    return <SectionStatus status={s.status === "error" ? "error" : "loading"} onRetry={() => void vm.load()} testID="features-settings-status" />;
  }
  return (
    <Stack gap="6" testID="features-settings">
      {s.canEdit ? null : <FieldNote text={t("features.adminsOnly")} testID="features-readonly" />}
      <SettingsSection title={t("features.title")} description={t("features.intro")} testID="features-section">
        {s.features.map((row) => {
          const copy = FEATURE_COPY[row.area];
          const note = row.needs !== undefined ? t("features.needsParent", { area: t(FEATURE_COPY[row.needs].title) }) : row.last ? t("features.lastOne") : undefined;
          return (
            <Stack key={row.area} gap="1" testID={`feature-${row.area}`}>
              <Toggle
                label={t(copy.title)}
                value={row.on && row.needs === undefined}
                onChange={(on) => void vm.setFeature(row.area, on)}
                disabled={!s.canEdit || s.featureSaving !== undefined || row.needs !== undefined || row.last}
                testID={`feature-toggle-${row.area}`}
              />
              <FieldNote text={t(copy.hint)} />
              {note === undefined ? null : <FieldNote text={note} testID={`feature-note-${row.area}`} />}
            </Stack>
          );
        })}
      </SettingsSection>
      <SettingsSection title={t("features.adminAccess.title")} description={t("features.adminAccess.hint")} testID="features-admin-access">
        <SegmentedControl<FeatureAdminAccess>
          options={[
            { value: "none", label: t("features.adminAccess.none") },
            { value: "read", label: t("features.adminAccess.read") },
            { value: "full", label: t("features.adminAccess.full") },
          ]}
          value={s.featureAdminAccess}
          onChange={(v) => (s.canEdit ? void vm.setFeatureAdminAccess(v) : undefined)}
          accessibilityLabel={t("features.adminAccess.title")}
          testID="features-admin-access-control"
        />
      </SettingsSection>
      <SettingsNotice notice={s.notice} onClose={() => vm.dismissNotice()} testID="features-notice" />
    </Stack>
  );
}
